// src/components/restaurant/menu/AdvancedMenuFormEdit.jsx
import React, { useEffect, useRef, useState } from "react";
import api from "../../../utils/api";
import { toast } from "react-toastify";
import { useSubcategories } from "../../../hooks/useSubcategories";
import { showToast } from "../../../helper/generalHelper";
import MenuVariantFormEdit from "./MenuVariantFormEdit";
import ImageUploadPreview from "../ImageUploadPreview";
import { useNavigate } from "react-router-dom";
import CategoryDropdown from "../../common/CategoryDropdown";

// const token = localStorage.getItem("token");
const supportedLanguages = ["en", "hi"];

const AdvancedMenuFormEdit = ({ inventory, menuData }) => {
  const { subcategories } = useSubcategories();
  const isEdit = !!menuData?.id;
  const navigate = useNavigate();

  const [categoryId, setCategoryId] = useState("");
  const [translations, setTranslations] = useState(
    supportedLanguages.reduce((acc, lang) => {
      acc[lang] = { title: "", shortDescription: "", description: "" };
      return acc;
    }, {})
  );
  const [images, setImages] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [variantForms, setVariantForms] = useState([`variant-${Date.now()}`]);
  const variantRefs = useRef([]);
  const [activeLang, setActiveLang] = useState("en");
  const [has_variation, setHasVariation] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagesItem, setImagesItem] = useState([]);

  // Transform API data to match component structure
  const transformMenuData = (data) => {
    if (!data) return null;

    return {
      id: data.id,
      categoryId: data.category_id,
      sku: data.sku,
      hasTax: (Number(data.has_tax) === 1 && parseFloat(data.tax) > 0) ? 1 : 0,
      tax: data.tax,
      mrp: data.mrp,
      price: data.price,
      quantity: data.quantity,
      images: data.images || [],
      uploads: data.uploads || [],
      menuImages: data.menuImages || [],
      inventory_manage: data.inventory_manage,
      ingredients: data.ingredients || [],
      translations: data.translations || {},
      has_variation: data.has_variation || 1,

      // Transform childMenus to variants
      variants: (data.childMenus || []).map((child) => ({
        id: child.id,
        categoryId: child.category_id,
        sku: child.sku,
        hasTax: (Number(child.has_tax) === 1 && parseFloat(child.tax) > 0) ? 1 : 0,
        tax: child.tax,
        mrp: child.mrp,
        price: child.price,
        quantity: child.quantity,
        images: child.images || [],
        uploads: child.uploads || [],
        menuImages: data.menuImages || [],
        inventory_manage: child.inventory_manage,
        ingredients: child.ingredients || [],
        translations: child.translations || {},
        has_variation: child.has_variation || 1,
      })),
    };
  };

  // Prefill parent + variants on edit
  useEffect(() => {
    if (!menuData) return;

    const transformedData = transformMenuData(menuData); 

    if (transformedData) {
      setCategoryId(transformedData.categoryId || "");
      setTranslations(
        supportedLanguages.reduce((acc, lang) => {
          acc[lang] = {
            title: transformedData.translations?.[lang]?.title || "",
            shortDescription:
              transformedData.translations?.[lang]?.shortDescription || "",
            description:
              transformedData.translations?.[lang]?.description || "",
          };
          return acc;
        }, {})
      );

      setImages(transformedData.images || []);
      setImagesItem(transformedData.menuImages || []);
      setHasVariation(transformedData.has_variation || 0);

      // Fix variant form initialization
      if (transformedData.variants && transformedData.variants.length > 0) {
        const variantKeys = transformedData.variants.map((v, index) =>
          v.id ? `variant-${v.id}` : `variant-new-${Date.now()}-${index}`
        );
        setVariantForms(variantKeys);
        variantRefs.current = [];
        // Auto-set has_variation to 1 if there are variants
        setHasVariation(1);
      } else {
        setVariantForms([`variant-new-${Date.now()}`]);
        variantRefs.current = [];
      }
    }
  }, [menuData]);

  const addVariantForm = () => {
    setVariantForms((prev) => [
      ...prev,
      `variant-new-${Date.now()}-${Math.random()}`,
    ]);
    // Auto-set has_variation to 1 when adding variants
    setHasVariation(1);
  };

  const removeVariantForm = (indexToRemove) => {
    setVariantForms((prev) => prev.filter((_, i) => i !== indexToRemove));
    variantRefs.current = variantRefs.current.filter(
      (_, i) => i !== indexToRemove
    );

    // If no variants left, set has_variation to 0
    if (variantForms.length <= 1) {
      setHasVariation(0);
    }
  };

  // Function to upload images
  const uploadImages = async (imageFiles) => {
    if (!imageFiles || imageFiles.length === 0) return [];

    const formData = new FormData();
    imageFiles.forEach((file) => {
      formData.append("images[]", file);
    });

    try {
      const response = await api.post(
        "restaurant/menu/upload-images",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
 

      // Fix: Properly extract image names from response
      if (response.data && response.data && response.data.images) {
        return response.data.images; // This should be the array of image filenames
      } else {
        console.error("Unexpected response structure:", response.data);
        toast.error("Unexpected response from server");
        return [];
      }
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error("Failed to upload images");
      return [];
    }
  };
 

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Build formData from state
      const formData = {
        categoryId,
        translations,
        has_variation,
        images,
      };

      // Collect variant refs
      const validRefs = Object.values(variantRefs.current).filter(
        (ref) => ref && typeof ref.getSanitizedData === "function"
      );

      const variants = validRefs.map((ref) => ref.getSanitizedData());
      const childUploads = validRefs.flatMap(
        (ref) => ref?.getUploads?.() || []
      );

      // Collect all files to upload
      const allUploadFiles = [...uploads, ...childUploads];

      // Upload files
      const uploadedImageNames = await uploadImages(allUploadFiles);

      // Keep track of existing images
      const existingParentImages =
        Array.isArray(formData.images) && formData.images.length > 0
          ? formData.images
          : [];

      const existingVariantImages = variants.map((v) =>
        Array.isArray(v.images) ? v.images : []
      );

      // Sequential assignment
      let imageIndex = 0;

      // Parent images
      const parentImages =
        uploads.length > 0
          ? uploadedImageNames.slice(imageIndex, imageIndex + uploads.length)
          : existingParentImages;

      imageIndex += uploads.length;

      // Parent menu object
      const firstVariant = variants[0] || {};
      const parentMenu = {
        ...(isEdit && menuData.id && { id: menuData.id }),
        categoryId: Number(formData.categoryId),
        sku: menuData.sku,
        mrp: firstVariant.mrp || 0,
        price: firstVariant.price || 0,
        quantity: firstVariant.quantity || 0,
        hasTax: firstVariant.hasTax ?? 0,
        tax: firstVariant.hasTax ? (firstVariant.tax || 0) : 0,
        inventory_manage: firstVariant.inventory_manage ?? 0,
        ingredients: firstVariant.ingredients || [],
        translations: formData.translations,
        images: parentImages,
        has_variation: formData.has_variation,
      };

      // Variants with images
      const variantsWithImages = variants.map((variant, index) => {
        const variantUploads = validRefs[index]?.getUploads?.() || [];
        const variantImages =
          variantUploads.length > 0
            ? uploadedImageNames.slice(
                imageIndex,
                imageIndex + variantUploads.length
              )
            : existingVariantImages[index];

        imageIndex += variantUploads.length;

        return {
          ...variant,
          images: variantImages,
          has_variation: 0,
        };
      });

      // Final menus list
      const menus = [parentMenu, ...variantsWithImages];

      // Ensure unique SKUs
      const skus = menus.map((menu) => menu.sku);
      if (new Set(skus).size !== skus.length) {
        toast.error("Please ensure all variants have unique SKUs.");
        return;
      }

      // Final payload with both new + existing images
      const payload = {
        menus,
        // uploads: [
        //   ...existingParentImages,
        //   ...uploadedImageNames,
        //   ...existingVariantImages.flat(),
        // ],
        uploads: [],
      };
 
      // Call API
      const endpoint = isEdit
        ? "restaurant/restaurant/menu/update"
        : "restaurant/restaurant/menu/new";

      const res = await api[isEdit ? "put" : "post"](endpoint, payload);
      showToast(res);
      if (res.success) {
        setTimeout(() => {
          const isStaff = window.location.pathname.includes("/restaurant-staff");
          navigate(isStaff ? "/restaurant-staff/employee/menu/items" : "/restaurant/menus/all");
        }, 1500);
      }
    } catch (err) {
      console.error("Submission error:", err);
      toast.error(
        err.response?.data?.message || "Submission failed. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get the transformed data for passing to variants
  const transformedMenuData = transformMenuData(menuData);

  return (
    <div className="space-y-6">
      {/* Parent Section */}
      <div className="p-6 border rounded bg-white shadow-md">
        <h2 className="text-lg font-semibold mb-4">
          {isEdit ? "Edit Menu Item" : "Create New Menu Item"}
          {isEdit && (
            <span className="text-sm text-gray-600 ml-2">
              (ID: {menuData.id})
            </span>
          )}
        </h2>

        <label className="block mb-2 font-semibold">Subcategory *</label>
        <CategoryDropdown
          value={categoryId}
          onChange={(val) => setCategoryId(val)}
          categories={subcategories}
          placeholder="Select Subcategory"
        />

        {/* Translations */}
        <div className="mb-4">
          <div className="flex gap-2 mb-3">
            {supportedLanguages.map((lang) => (
              <button
                key={lang}
                type="button"
                onClick={() => setActiveLang(lang)}
                className={`px-4 py-1 rounded text-sm border ${
                  activeLang === lang
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-800"
                }`}
              >
                {lang.toUpperCase()}
              </button>
            ))}
          </div>
             {[{ label: "Item Name", field: "title" },{ label:"shortDescription", field: "shortDescription" },{ label:"description", field: "description" }].map(({field, label }) => {
            const Component = field === "description" ? "textarea" : "input";
            label =
              label === "shortDescription"
                ? "Short Description"
                : label.charAt(0).toUpperCase() + label.slice(1);
            return (
              <div key={field} className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
                  {label} ({activeLang}) {field === "title" && "*"}
                </label>
                <Component
                  placeholder={`Enter ${field} in ${activeLang}`}
                  value={translations[activeLang][field]}
                  onChange={(e) =>
                    setTranslations((prev) => ({
                      ...prev,
                      [activeLang]: {
                        ...prev[activeLang],
                        [field]: e.target.value,
                      },
                    }))
                  }
                  className="border p-2 w-full rounded"
                  rows={field === "description" ? 4 : undefined}
                />
              </div>
            );
          })}
        </div>

        <ImageUploadPreview
          initialImages={menuData?.uploads}
          listImage={imagesItem}
          onChange={(imgs) => setImages(imgs)} // This should be image names
          onUploads={(files) => setUploads(files)} // This should be File objects
        />
      </div>

      {/* Variant Forms - Only show if has_variation is 1 */}
      {has_variation === 1 &&
        variantForms.map((id, index) => {
          const variant = transformedMenuData?.variants?.[index] || null;
          return (
            <MenuVariantFormEdit
              key={id}
              ref={(el) => {
                if (el) {
                  variantRefs.current[index] = el;
                }
              }}
              inventory={inventory}
              categoryId={categoryId}
              menuData={variant}
              onDelete={
                index === 0 ? undefined : () => removeVariantForm(index)
              }
              index={index}
              isEdit={isEdit}
            />
          );
        })}

      {/* Add Variant button - Only show if has_variation is 1 */}
      {has_variation === 1 && (
        <div className="flex gap-4 mt-4">
          <button
            type="button"
            onClick={addVariantForm}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
          >
            + Add Variant
          </button>
        </div>
      )}

      <div className="flex gap-4 mt-4">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors ${
            isSubmitting ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isSubmitting
            ? "Submitting..."
            : isEdit
            ? "Update Menu"
            : "Create Menu"}
        </button>
      </div>
    </div>
  );
};

export default AdvancedMenuFormEdit;
